import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getFlavorInfo } from "./flavorSlice";
import { api_url, web_client_url, environment } from "../config.js";
import axios from "axios";

const fake_token =
  "eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOi9hay5leGFtcGxlLmNvbS9hdXRoL3JlYWxttV7Y3cskztDZqqmQI";

const getAuthToken = () => {
  const keycloak = JSON.parse(localStorage.getItem("keycloak") || "{}");
  return environment === "production-no-auth" ? fake_token : keycloak?.token;
};

export const listAllDesktops = createAsyncThunk(
  "desktop/allList",
  async (_, { rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      const { data } = await axios.get(
        `${api_url}/desktops?allUsers=true`,
        config,
      );
      return data; // returns List<OutDesktopDto>
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail || "Error: Listing all desktops failed.",
      );
    }
  },
);

export const listMyDesktops = createAsyncThunk(
  "desktop/myList",
  async (_, { rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      const { data } = await axios.get(`${api_url}/desktops`, config);
      return data; // returns List<OutDesktopDto>
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail || "Error: Listing my desktops failed.",
      );
    }
  },
);

export const desktopDetails = createAsyncThunk(
  "desktop/details",
  async (id, { dispatch, rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      const { data } = await axios.get(`${api_url}/desktops/${id}`, config);

      let flavorInfo = null;
      if (data?.flavorId) {
        const result = await dispatch(getFlavorInfo(data.flavorId));
        if (getFlavorInfo.fulfilled.match(result)) {
          flavorInfo = result.payload;
        }
      }
      return {
        desktop: data,
        web_client_url: web_client_url,
        flavor: flavorInfo,
      };
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail ||
          "Error: Getting desktop details failed.",
      );
    }
  },
);

export const desktopOpenLocally = createAsyncThunk(
  "desktop/openLocally",
  async (desktopData, { rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      const { data } = await axios.post(
        `${api_url}/desktops?run_locally=true`,
        desktopData,
        config,
      );
      return data;
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail ||
          "Error: Failed to open desktop locally.",
      );
    }
  },
);

export const desktopCreate = createAsyncThunk(
  "desktop/create",
  async (desktopData, { rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };
    try {
      const { data } = await axios.post(
        `${api_url}/desktops`,
        {
          PendingAction: "Start",
          ...desktopData,
        },
        config,
      );
      return data; // returns the created OutDesktopDto (including ID and initial state)
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.errors ||
          error.response?.data?.detail ||
          error.response?.data ||
          "Error: Desktop creation failed.",
      );
    }
  },
);

export const desktopUpdate = createAsyncThunk(
  "desktop/update",
  async ({ id, desktopData }, { rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      const { data } = await axios.patch(
        `${api_url}/desktops/${id}`,
        desktopData,
        config,
      );
      return data; // returns the updated OutDesktopDto
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail || "Error: Desktop update failed.",
      );
    }
  },
);

export const desktopDelete = createAsyncThunk(
  "desktop/delete",
  async (id, { rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      await axios.delete(`${api_url}/desktops/${id}`, config);
      return id; // to update the state after deletion
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail || "Error: Desktop deletion failed.",
      );
    }
  },
);

export const desktopSchema = createAsyncThunk(
  "desktop/desktopSchema",
  async (_, { rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      const { data } = await axios.get(`${api_url}/openapi.json`, config);
      const schema = data.components.schemas.Desktop;
      return schema;
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail || "Error: An unexpected error occurred.",
      );
    }
  },
);

export const desktopPersist = createAsyncThunk(
  "desktop/persist",
  async ({ id, persistData }, { rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      const { data } = await axios.post(
        `${api_url}/desktops/${id}/persist`,
        persistData,
        config,
      );
      return data;
    } catch (error) {
      if (environment === "development") {
        console.error("Error persisting desktop:", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail || "An unexpected error occurred.",
      );
    }
  },
);

export const fetchPersistProgress = createAsyncThunk(
  "desktop/persistProgress",
  async (id, { rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      const { data } = await axios.get(
        `${api_url}/desktops/${id}/persist`,
        config,
      );
      return data;
    } catch (error) {
      if (environment === "development") {
        console.error("Error fetching persist progress:", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail || "Error fetching persist progress.",
      );
    }
  },
);

const initialState = {
  desktops: [],
  desktop: {},
  desktop_schema: {},
  flavor: {},
  loading: false,
  error: null,
  web_client_url: "",
};

const desktopSlice = createSlice({
  name: "desktop",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // desktop details
      .addCase(desktopDetails.pending, (state) => {
        state.loading = true;
        state.desktop = {};
        state.flavor = {};
        state.error = null;
      })
      .addCase(desktopDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.desktop = action.payload.desktop;
        state.web_client_url = action.payload.web_client_url;
        state.flavor = action.payload.flavor;
      })

      // open desktop locally
      .addCase(desktopOpenLocally.fulfilled, (state) => {
        state.loading = false;
      })

      // create desktop
      .addCase(desktopCreate.fulfilled, (state, action) => {
        state.loading = false;
        // prevent duplicates if SSE and POST response arrive simultaneously
        const exists = state.desktops.find((d) => d.id === action.payload.id);
        if (!exists) state.desktops.push(action.payload);
      })

      // update desktop (used by both action buttons and SSE state changes)
      .addCase(desktopUpdate.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.desktops.findIndex(
          (d) => d.id === action.payload.id,
        );
        if (index !== -1) {
          state.desktops[index] = action.payload; // update the card with the new OutDesktopDto
        }
        state.desktop = action.payload;
      })

      // delete desktop
      .addCase(desktopDelete.fulfilled, (state, action) => {
        state.loading = false;
        state.desktops = state.desktops.filter((d) => d.id !== action.payload); // action.payload is the ID
        state.desktop = {};
      })

      // desktop schema
      .addCase(desktopSchema.fulfilled, (state, action) => {
        state.loading = false;
        state.desktop_schema = action.payload;
      })

      // persist desktop
      .addCase(desktopPersist.fulfilled, (state) => {
        state.loading = false;
      })

      // list all desktops & my desktops
      .addMatcher(
        (action) =>
          action.type === listAllDesktops.fulfilled.type ||
          action.type === listMyDesktops.fulfilled.type,
        (state, action) => {
          state.loading = false;
          state.desktops = Array.isArray(action.payload) ? action.payload : [];
        },
      )

      // global loading & error handling for desktop actions "pending" and "rejected"
      .addMatcher(
        (action) =>
          action.type.startsWith("desktop/") &&
          action.type.endsWith("/pending") &&
          !action.type.includes("persistProgress"),
        (state) => {
          state.loading = true;
          state.error = null;
        },
      )
      .addMatcher(
        (action) =>
          action.type.startsWith("desktop/") &&
          action.type.endsWith("/rejected") &&
          !action.type.includes("persistProgress"),
        (state, action) => {
          state.loading = false;
          state.error = action.payload;
        },
      );
  },
});

export default desktopSlice.reducer;

export { getFlavorInfo } from "./flavorSlice";
